import { Anchor, Box, Flex, Progress, Text } from "@mantine/core";
import { IChecklist, IChecklistPopulated, IChecklistProgress } from "@mctypes/checklist.types";
import { FlatHierarchy, IPNSProps, IValueAndColor } from "@mctypes/global.types";
import { IChecklistStatus } from "@mcapps/shared/schemas/checklistSchema";
import { IUserPartial } from "@schemas/issueSchema";
import { ColDef } from "ag-grid-community";
import { Avatar, OptionBadge } from "../../../components";
import { SimpleTagsMantine } from "../../../components/SimpleTags/SimpleTagsMantine";
import { UserCellRenderer } from "../../../components/ConnectedIssues/ConnectedIssues";
import { STANDARD_COLORS } from "../../globalConstants";
import { formatShortDate } from "../admin/selectors";
import { convertValueAndColorToMultiDisplayColor } from "../issuesApp/issueUtils";
import { CHECKLIST_STATES_OPTIONS, CHECKLIST_STATES_OPTIONS_COLOR } from "./constants";

export const StatusBadgeRenderer = (params) => {
  return (
    <OptionBadge
      activeOption={(params?.value as IValueAndColor)?.value}
      options={convertValueAndColorToMultiDisplayColor(CHECKLIST_STATES_OPTIONS_COLOR)}
    />
  );
};

const userComparator: (a: IUserPartial, b: IUserPartial) => number = (a, b) => {
  if (!a && !b) return 0;
  if (!a) return -1;
  if (!b) return 1;
  if (a.name == b.name) return 0;
  return a.name > b.name ? 1 : -1;
};

export const getChecklistColumns = (
  isTemplate: boolean,
  pnsItems: FlatHierarchy<IPNSProps>,
  allTags: string[],
  onUpdateTags: (checklistId: string, oldTags: string[], newTags: string[]) => void | Promise<void>,
) => {
  const checklistColumns = {
    id: {
      headerName: "ID",
      field: "id",
      sort: "desc",
      resizable: false,
      width: 100,
      cellRenderer: (params) => {
        // get the url from the address bar
        const url = window.location.href;
        return (
          <Anchor fw={500} href={`${url}/${params.data._id}`}>
            {params.value}
          </Anchor>
        );
      },
    } as ColDef<IChecklist>,
    status: {
      headerName: "Status",
      field: "status",
      sortable: true,
      minWidth: 120,
      cellClass: "cell-px0",
      valueGetter: (params) => {
        const status = CHECKLIST_STATES_OPTIONS_COLOR.find(
          (option) =>
            CHECKLIST_STATES_OPTIONS.find((opt) => opt.value === params.data.status).displayValue === option.value,
        );
        return status;
      },
      valueSetter: (params) => {
        return params.newValue.displayValue;
      },
      cellEditor: "agRichSelectCellEditor",
      cellEditorParams: {
        values: CHECKLIST_STATES_OPTIONS_COLOR,
        cellRenderer: StatusBadgeRenderer,
        valueListMaxHeight: 220,
      },
      width: 130,
      cellRenderer: StatusBadgeRenderer,
      filterValueGetter: (params) => {
        const status = CHECKLIST_STATES_OPTIONS_COLOR.find(
          (option) =>
            CHECKLIST_STATES_OPTIONS.find((opt) => opt.value === params.data.status).displayValue === option.value,
        );
        return status.value.toUpperCase();
      },
      comparator: (valueA: IChecklistStatus, valueB: IChecklistStatus) => {
        if (valueA == valueB) return 0;
        return valueA > valueB ? 1 : -1;
      },
      filter: "agSetColumnFilter",
    } as ColDef<IChecklist>,
    name: {
      headerName: "Tittel",
      field: "name",
      filter: "agTextColumnFilter",
      minWidth: 150,
      autoHeight: true,
    } as ColDef<IChecklist>,
    progress: {
      headerName: "Fremdrift",
      field: "progress",
      width: 130,
      minWidth: 130,
      hide: isTemplate,
      cellRenderer: (params) => {
        const progress = params.value as IChecklistProgress;
        const accepted = Math.max(0, progress?.accepted ?? 0);
        const rejected = Math.max(0, progress?.rejected ?? 0);

        return (
          <Box w="100%">
            {progress != null && (
              <Progress.Root size={10} radius={4}>
                <Progress.Section value={accepted} color={STANDARD_COLORS.green} />
                <Progress.Section value={rejected} color={STANDARD_COLORS.red} />
              </Progress.Root>
            )}
          </Box>
        );
      },
      comparator: (valueA: IChecklistProgress, valueB: IChecklistProgress) => {
        const totalA = (valueA?.accepted ?? 0) + (valueA?.rejected ?? 0);
        const totalB = (valueB?.accepted ?? 0) + (valueB?.rejected ?? 0);
        return totalA - totalB;
      },
    } as ColDef<IChecklist>,
    pns: {
      headerName: "PNS",
      field: "pns",
      filter: "agSetColumnFilter",
      hide: isTemplate,
      cellRenderer: (params) => {
        if (params.value != null) {
          return (
            <div className="truncateHeaderText">
              {params.value} - {pnsItems.find((pns) => pns.props.pnsId === params.value)?.props?.title}
            </div>
          );
        } else {
          return "";
        }
      },
    } as ColDef<IChecklist>,
    createdAt: {
      headerName: "Opprettet",
      field: "createdAt",
      filter: "agDateColumnFilter",
      maxWidth: 140,
      initialSort: "desc",
      cellRenderer: (params) => formatShortDate(params.value),
    } as ColDef<IChecklist>,
    dueDate: {
      headerName: "Frist",
      field: "dueDate",
      filter: "agDateColumnFilter",
      maxWidth: 140,
      hide: isTemplate,
      cellRenderer: (params) => {
        if (params.value != null) {
          return params.data.status === "ACTIVE" && new Date(params.value) < new Date() ? (
            <Text c="red">{formatShortDate(params.value)}</Text>
          ) : (
            formatShortDate(params.value)
          );
        } else {
          return "";
        }
      },
    } as ColDef<IChecklist>,
    assignedTo: {
      headerName: "Ansvarlig",
      field: "assignedTo",
      hide: isTemplate,
      minWidth: 100,
      width: 150,
      cellClass: "cell-p0",
      filter: "agSetColumnFilter",
      valueGetter: (params) => {
        return (params as any)?.data?.assignedTo?.name;
      },
      cellRenderer: (params) => {
        const owner = params?.data?.assignedTo;
        return owner != null ? (
          <Flex style={{ alignItems: "center" }}>
            <Avatar id={owner._id} name={owner.name} />
            <Box ml="0.5rem">{owner.name}</Box>
          </Flex>
        ) : (
          <div></div>
        );
      },
    } as ColDef<IChecklist>,
    tags: {
      headerName: "Etiketter",
      field: "tags",
      minWidth: 100,
      cellClass: "grid-display-block cell-p0",
      autoHeight: true,
      cellRenderer: (params) => (
        <SimpleTagsMantine
          editable={false}
          allowNew={false}
          activeTags={(params.value as string[]) ?? []}
          tagOptions={allTags}
          tagSize="sm"
          tagColor="warmgray.4"
          showTagIcon={false}
          maxVisibleTags={3}
          onChange={(newTags) => onUpdateTags(params.data?._id, (params.value as string[]) ?? [], newTags)}
        />
      ),
      filter: "agSetColumnFilter",
      filterParams: {
        values: allTags,
      },
    } as ColDef<IChecklist>,
    template: {
      filter: "agSetColumnFilter",
      headerName: "Mal",
      field: "generatedFromTemplate.name",
      minWidth: 200,
    } as ColDef<IChecklist>,
    competenceNetwork: {
      filter: "agSetColumnFilter",
      headerName: "KNV",
      field: "competenceNetwork",
      minWidth: 100,
    } as ColDef<IChecklist>,
    doksamId: {
      filter: "agSetColumnFilter",
      headerName: "Core Dokument-ID",
      field: "doksam.docId",
      minWidth: 100,
    } as ColDef<IChecklist>,
  } as const;

  if (isTemplate === true) {
    const checklistTemplateColumns = {
      documentManagersPreparedBy: {
        headerName: "Utarbeidet av",
        field: "documentManagers.preparedBy",
        filter: "agSetColumnFilter",
        cellRenderer: (params) => <UserCellRenderer height="15px" user={params.value} />,
        filterValueGetter: (params) => (params.data as IChecklistPopulated).documentManagers?.preparedBy?.name,
        comparator: userComparator,
      } as ColDef<IChecklist>,
      documentManagersCheckedBy: {
        headerName: "Kontrollert av",
        field: "documentManagers.checkedBy",
        filter: "agSetColumnFilter",
        cellRenderer: (params) => <UserCellRenderer height="15px" user={params.value} />,
        filterValueGetter: (params) => (params.data as IChecklistPopulated).documentManagers?.checkedBy?.name,
        comparator: userComparator,
      } as ColDef<IChecklist>,
      documentManagersApprovedBy: {
        headerName: "Godkjent av",
        field: "documentManagers.approvedBy",
        filter: "agSetColumnFilter",
        cellRenderer: (params) => <UserCellRenderer height="15px" user={params.value} />,
        filterValueGetter: (params) => (params.data as IChecklistPopulated).documentManagers?.approvedBy?.name,
        comparator: userComparator,
      } as ColDef<IChecklist>,
      checklistAdmin: {
        headerName: "Admin",
        field: "admin",
        filter: "agSetColumnFilter",
        cellRenderer: (params) => <UserCellRenderer height="15px" user={params.value} />,
        filterValueGetter: (params) => (params.data as IChecklistPopulated).admin?.name,
        comparator: userComparator,
      } as ColDef<IChecklist>,
      disciplineLeader: {
        headerName: "FLF",
        field: "disciplineLeader",
        filter: "agSetColumnFilter",
        cellRenderer: (params) => <UserCellRenderer height="15px" user={params.value} />,
        filterValueGetter: (params) => (params.data as IChecklistPopulated).disciplineLeader?.name,
        comparator: userComparator,
      } as ColDef<IChecklist>,
      disciplineCode: {
        headerName: "Fagdisiplin",
        field: "disciplineCode",
        filter: "agSetColumnFilter",
      } as ColDef<IChecklist>,
      subDiscipline: {
        headerName: "Delfagområde",
        field: "subDiscipline",
        filter: "agSetColumnFilter",
      } as ColDef<IChecklist>,
      documentCategoryCode: {
        headerName: "Dokumenttype",
        field: "documentCategoryCode",
        filter: "agSetColumnFilter",
      } as ColDef<IChecklist>,
      documentCategoryCode2: {
        headerName: "Dokumenttype 2",
        field: "documentCategoryCode2",
        filter: "agSetColumnFilter",
        hide: true,
      } as ColDef<IChecklist>,
      revisionNr: {
        headerName: "Revisionsnr",
        field: "revisionNr",
        filter: "agSetColumnFilter",
        hide: true,
      } as ColDef<IChecklist>,
      revisionDate: {
        headerName: "Revisionsdato",
        field: "revisionDate",
        filter: "agDateColumnFilter",
      } as ColDef<IChecklist>,
    } as const;

    const checklistTemplateColumnDefs: ColDef<IChecklist>[] = [
      checklistColumns.id,
      checklistColumns.status,
      checklistColumns.competenceNetwork,
      checklistTemplateColumns.subDiscipline,
      checklistTemplateColumns.disciplineCode,
      checklistColumns.name,
      checklistColumns.createdAt,
      checklistTemplateColumns.revisionDate,
      checklistTemplateColumns.documentManagersPreparedBy,
      checklistTemplateColumns.documentManagersCheckedBy,
      checklistTemplateColumns.documentManagersApprovedBy,
      checklistTemplateColumns.disciplineLeader,
      checklistTemplateColumns.checklistAdmin,
      checklistColumns.tags,
      checklistTemplateColumns.documentCategoryCode,
      checklistTemplateColumns.documentCategoryCode2,
      checklistTemplateColumns.revisionNr,
    ];

    return checklistTemplateColumnDefs;
  }

  const checklistColumnDefs: ColDef<IChecklist>[] = [
    checklistColumns.id,
    checklistColumns.status,
    checklistColumns.name,
    checklistColumns.progress,
    checklistColumns.pns,
    checklistColumns.dueDate,
    checklistColumns.assignedTo,
    checklistColumns.tags,
    checklistColumns.competenceNetwork,
    checklistColumns.template,
    checklistColumns.doksamId,
  ];

  return checklistColumnDefs;
};
